import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download, Trash2, Upload, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const readB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});
const prettySize = (b) => (b == null ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

const expiryStatus = (exp) => {
  if (!exp) return { label: "No expiry set", cls: "bg-slate-100 text-slate-500" };
  const d = new Date(exp); const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.floor((d - today) / 86400000);
  if (days < 0) return { label: "Expired", cls: "bg-rose-100 text-rose-700" };
  if (days <= 30) return { label: "Expiring soon", cls: "bg-amber-100 text-amber-700" };
  return { label: "Valid", cls: "bg-emerald-100 text-emerald-700" };
};

export default function SupplierDocuments({ supplierId, onExpiryChange }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get(`/suppliers/${supplierId}/documents`).then((r) => setFiles(r.data)).catch(() => {});
  }, [supplierId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setUploading(true);
    try {
      let detected = 0;
      for (const file of list) {
        const b64 = await readB64(file);
        const { data } = await api.post(`/suppliers/${supplierId}/documents`, { filename: file.name, content_type: file.type, file_base64: b64, doc_type: "authorization_letter" });
        // Ask the AI to read the letter and auto-fill the expiry date.
        try {
          const scan = await api.post(`/suppliers/${supplierId}/documents/${data.id}/scan-expiry`);
          if (scan.data?.detected) detected += 1;
        } catch { /* non-fatal */ }
      }
      toast.success(detected ? `Uploaded — AI detected expiry on ${detected} letter(s)` : (list.length > 1 ? `${list.length} documents uploaded` : "Document uploaded"));
      load(); onExpiryChange && onExpiryChange();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const setExpiry = async (f, expires_on) => {
    setFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, expires_on } : x)));
    try {
      await api.patch(`/suppliers/${supplierId}/documents/${f.id}`, { expires_on });
      onExpiryChange && onExpiryChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/suppliers/${supplierId}/documents/${f.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: f.content_type }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.filename}?`)) return;
    await api.delete(`/suppliers/${supplierId}/documents/${f.id}`); load(); onExpiryChange && onExpiryChange(); toast.success("Removed");
  };

  return (
    <div className="rounded-lg border border-border bg-white p-6" data-testid="supplier-documents">
      <div className="mb-1 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[#1B4383]" />
        <h3 className="font-heading text-sm font-bold tracking-tight">Authorisation Letters</h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">Upload the OEM authorisation letter(s) and set each one's expiry date. You can add more than one.</p>

      <div className="space-y-2">
        {files.map((f) => {
          const st = expiryStatus(f.expires_on);
          return (
            <div key={f.id} className="rounded-md border border-border p-2.5" data-testid={`supplier-doc-${f.id}`}>
              <div className="flex items-center justify-between gap-2">
                <button onClick={() => download(f)} className="flex min-w-0 items-center gap-2 text-left" data-testid={`open-doc-${f.id}`}>
                  <FileText className="h-4 w-4 shrink-0 text-[#1B4383]" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-slate-800">{f.filename}</span>
                    <span className="block text-xs text-muted-foreground">{prettySize(f.size)} · {f.uploaded_by_name || "—"}</span>
                  </span>
                </button>
                <div className="flex shrink-0 items-center gap-2">
                  <button onClick={() => download(f)} className="text-slate-400 hover:text-[#1B4383]" data-testid={`download-doc-${f.id}`}><Download className="h-4 w-4" /></button>
                  <button onClick={() => remove(f)} className="text-slate-400 hover:text-destructive" data-testid={`delete-doc-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500">Expiry</span>
                <Input type="date" value={f.expires_on ? String(f.expires_on).slice(0, 10) : ""} onChange={(e) => setExpiry(f, e.target.value)} className="h-8 w-40 text-xs" data-testid={`doc-expiry-${f.id}`} />
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${st.cls}`} data-testid={`doc-status-${f.id}`}>{st.label}</span>
              </div>
            </div>
          );
        })}
        {!files.length && <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground" data-testid="supplier-doc-empty">No authorisation letter uploaded yet.</p>}
      </div>

      <label className="mt-3 block">
        <input type="file" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="supplier-doc-upload-input" />
        <Button asChild variant="outline" size="sm" className="w-full" disabled={uploading}>
          <span>{uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Upload letter(s)</span>
        </Button>
      </label>
    </div>
  );
}
